import { useCallback } from 'react';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useTranslation } from 'react-i18next';
import {
    ButtonPropsModal,
    ItemsFooterType,
} from '@celebration/react/ClbModal/ClbModal.types';
import IConfirmationModal from './interfaces/IConfirmationModal';

function ConfirmationModal({
    isOpen,
    title,
    description,
    actionLink,
    confirmButtonText,
    cancelButtonText,
    onConfirm,
    onCancel,
    isLoading = false,
    confirmDisabled = false,
    modalType = 'warning',
    children,
}: Readonly<IConfirmationModal>) {
    const { t } = useTranslation();

    const handleCancel = useCallback(() => {
        if (!isLoading) {
            onCancel();
        }
    }, [isLoading, onCancel]);

    const handleConfirm = useCallback(() => {
        if (!isLoading && !confirmDisabled) {
            onConfirm();
        }
    }, [isLoading, confirmDisabled, onConfirm]);

    const cancelButton: ButtonPropsModal = {
        key: 'cancel-button',
        id: 'cancel-button',
        label: cancelButtonText ?? t('Cancelar'),
        disabled: isLoading,
        loading: false,
        title: cancelButtonText ?? t('Cancelar'),
        'data-testid': 'confirmation-modal-cancel-button',
        onClick: handleCancel,
        role: 'button',
        size: 'md',
        styleType: 'secondary',
    };

    const confirmButton: ButtonPropsModal = {
        key: 'confirm-button',
        id: 'confirm-button',
        label: confirmButtonText ?? t('Confirmar'),
        disabled: confirmDisabled,
        loading: isLoading,
        title: confirmButtonText ?? t('Confirmar'),
        'data-testid': 'confirmation-modal-confirm-button',
        onClick: handleConfirm,
        role: 'button',
        size: 'md',
        styleType: modalType === 'danger' ? 'danger' : 'primary',
    };

    const buttonsModal: ItemsFooterType = [confirmButton, cancelButton];

    const isStringDescription =
        typeof description === 'string' && !actionLink && !children;

    const renderCustomBody = () => (
        <>
            {description != null && (
                <p className="clb-modal--description">{description}</p>
            )}
            {actionLink && (
                <p className="clb-modal--description">{actionLink}</p>
            )}
            {children}
        </>
    );

    return (
        <ClbModal
            title={title}
            open={isOpen}
            onClose={handleCancel}
            description={isStringDescription ? description : undefined}
            customBody={isStringDescription ? undefined : renderCustomBody}
            type={modalType}
            itemsFooter={buttonsModal}
            data-testid="confirmation-modal"
        />
    );
}

export default ConfirmationModal;
